import React, { useState } from 'react';
import { AlertTriangle, CheckCircle, Clock } from 'lucide-react';
import { format } from 'date-fns';
import { systemAuditService } from '../../../services/systemAuditService';

export default function AlertsPanel({ alerts, onRefresh }) {
  const [processingAlert, setProcessingAlert] = useState(null);
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [showNotesModal, setShowNotesModal] = useState(null);

  const getSeverityColor = (severity) => {
    const colorMap = {
      critical: 'bg-red-100 text-red-800 border-red-300',
      high: 'bg-orange-100 text-orange-800 border-orange-300',
      medium: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      low: 'bg-blue-100 text-blue-800 border-blue-300'
    };
    return colorMap?.[severity] || 'bg-gray-100 text-gray-800 border-gray-300';
  };

  const getAlertTypeIcon = (alertType) => {
    return AlertTriangle;
  };

  const handleAcknowledge = async (alertId) => {
    try {
      setProcessingAlert(alertId);
      await systemAuditService?.updateAlertStatus(alertId, 'acknowledged');
      onRefresh();
    } catch (error) {
      console.error('Error acknowledging alert:', error);
    } finally {
      setProcessingAlert(null);
    }
  };

  const handleResolve = async (alertId) => {
    try {
      setProcessingAlert(alertId);
      await systemAuditService?.updateAlertStatus(alertId, 'resolved', resolutionNotes);
      setShowNotesModal(null);
      setResolutionNotes('');
      onRefresh();
    } catch (error) {
      console.error('Error resolving alert:', error);
    } finally {
      setProcessingAlert(null);
    }
  };

  const handleDismiss = async (alertId) => {
    try {
      setProcessingAlert(alertId);
      await systemAuditService?.updateAlertStatus(alertId, 'dismissed');
      onRefresh();
    } catch (error) {
      console.error('Error dismissing alert:', error);
    } finally {
      setProcessingAlert(null);
    }
  };

  if (!alerts || alerts?.length === 0) {
    return (
      <div className="text-center py-12">
        <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
        <p className="text-gray-500">No active alerts - System is healthy</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {alerts?.map((alert) => {
        const AlertIcon = getAlertTypeIcon(alert?.alertType);
        return (
          <div
            key={alert?.id}
            className={`border-2 rounded-lg p-4 ${getSeverityColor(alert?.severity)}`}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center space-x-3 mb-2">
                  <AlertIcon className="w-5 h-5" />
                  <span className="px-3 py-1 bg-white rounded-full text-xs font-bold uppercase">
                    {alert?.severity}
                  </span>
                  <span className="text-xs opacity-75">
                    <Clock className="w-3 h-3 inline mr-1" />
                    {format(new Date(alert?.createdAt), 'MMM dd, yyyy HH:mm:ss')}
                  </span>
                </div>
                <h3 className="text-lg font-bold mb-2">{alert?.title}</h3>
                <p className="text-sm mb-3">{alert?.description}</p>
                <div className="flex items-center space-x-4 text-xs">
                  <span>Type: {alert?.alertType?.replace(/_/g, ' ')}</span>
                  <span>Source: {alert?.sourceType?.replace(/_/g, ' ')}</span>
                  {alert?.affectedUsersCount > 0 && (
                    <span>Affected Users: {alert?.affectedUsersCount}</span>
                  )}
                </div>
              </div>
              <div className="ml-4 flex flex-col space-y-2">
                {alert?.status === 'active' && (
                  <>
                    <button
                      onClick={() => handleAcknowledge(alert?.id)}
                      disabled={processingAlert === alert?.id}
                      className="px-3 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700 disabled:opacity-50"
                    >
                      Acknowledge
                    </button>
                    <button
                      onClick={() => setShowNotesModal(alert?.id)}
                      disabled={processingAlert === alert?.id}
                      className="px-3 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700 disabled:opacity-50"
                    >
                      Resolve
                    </button>
                    <button
                      onClick={() => handleDismiss(alert?.id)}
                      disabled={processingAlert === alert?.id}
                      className="px-3 py-1 bg-gray-600 text-white rounded text-xs hover:bg-gray-700 disabled:opacity-50"
                    >
                      Dismiss
                    </button>
                  </>
                )}
                {alert?.status === 'acknowledged' && (
                  <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded text-xs font-medium">
                    Acknowledged
                  </span>
                )}
              </div>
            </div>

            {alert?.metadata && Object.keys(alert?.metadata)?.length > 0 && (
              <div className="mt-4 pt-4 border-t border-current opacity-50">
                <h4 className="text-xs font-semibold mb-2">Additional Details</h4>
                <pre className="bg-white bg-opacity-50 p-2 rounded text-xs overflow-auto max-h-32">
                  {JSON.stringify(alert?.metadata, null, 2)}
                </pre>
              </div>
            )}
          </div>
        );
      })}
      {/* Resolution Notes Modal */}
      {showNotesModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-bold mb-4">Resolve Alert</h3>
            <textarea
              value={resolutionNotes}
              onChange={(e) => setResolutionNotes(e?.target?.value)}
              placeholder="Enter resolution notes..."
              className="w-full border border-gray-300 rounded-lg p-3 mb-4 h-32"
            />
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowNotesModal(null);
                  setResolutionNotes('');
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => handleResolve(showNotesModal)}
                disabled={processingAlert === showNotesModal}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                Resolve
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}